import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  GetCompanySearchResponse,
  GetDirectorSearchResponse,
} from '@iwoca/lapi-client/edge';
import {
  Button,
  Card,
  Icon,
  Input,
  Select,
  CheckBox,
  Message,
  Modal,
  Tooltip,
} from '@iwoca/orion';
import clsx from 'clsx';
import { CompanyInputUK } from 'components/Signup/NewSignupForm/CompanyInputUK/CompanyInputUK';
import { DirectorSearch } from 'components/Signup/NewSignupForm/DirectorSearch/DirectorSearch';
import { useAmplitude } from 'hooks/useAmplitude';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { TIntroducerType, TRegistrationFormDataUK } from './registration.types';
import styles from './RegistrationForm.module.css';
import { generateUKAddress, submitRegistration } from './registrationHelper';
import {
  fullRegistrationSchemaUK,
  personalInformationSchemaUK,
} from './registrationSchema';

export const RegistrationFormUK = () => {
  const [searchParams] = useSearchParams();
  const { trackEvent } = useAmplitude();
  const inviteId = searchParams.get('introducer') as string | undefined;
  const introducerTypeFromQueryParameter: TIntroducerType =
    searchParams.get('introducerType') === 'accountant'
      ? 'accountant'
      : 'broker';
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [introducerType, setIntroducerType] = useState<TIntroducerType>();
  const [isCompanySearch, setIsCompanySearch] = useState(true);
  const [selectedCompany, setSelectedCompany] =
    useState<GetCompanySearchResponse['data'][number]>();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<TRegistrationFormDataUK>({
    defaultValues: {
      introducerType: introducerTypeFromQueryParameter,
    },
    resolver: yupResolver(
      inviteId ? personalInformationSchemaUK : fullRegistrationSchemaUK,
    ),
  });

  const handleToggleCompanyInputMode = () => {
    setIsCompanySearch(!isCompanySearch);

    // Unset values
    setValue('companyName', '');
    setValue('companyNumber', '');
    setValue('firstName', '');
    setValue('lastName', '');
  };

  const handleCompanySearchSelect = (
    company?: GetCompanySearchResponse['data'][number],
  ) => {
    setSelectedCompany(company);
    setValue('companyName', company?.name || '');
    setValue('companyNumber', company?.number || '');

    // Unset director
    setValue('firstName', '');
    setValue('lastName', '');
  };

  const handleDirectorSearchSelect = (
    director?: GetDirectorSearchResponse['data'][number],
  ) => {
    setValue('firstName', director?.first_name || '');
    setValue('lastName', director?.last_name || '');
  };

  const handleChangeIntroducerType = (newIntroducerType: TIntroducerType) => {
    if (
      (newIntroducerType === 'accountant' && isCompanySearch) ||
      (newIntroducerType === 'broker' && !isCompanySearch)
    ) {
      handleToggleCompanyInputMode();
    }
    setIntroducerType(newIntroducerType);
  };

  useEffect(() => {
    if (introducerTypeFromQueryParameter) {
      setIntroducerType(introducerTypeFromQueryParameter);
    }
  }, [introducerTypeFromQueryParameter]);

  const onSubmit = async (values: TRegistrationFormDataUK) => {
    setIsLoading(true);

    const handleSuccess = () => {
      trackEvent(AMPLITUDE_EVENTS.introducerRegisterSubmitted, {
        'Introducer type': values.introducerType,
        'Invite link': inviteId || null,
      });

      navigate(PATH_NAMES.home());
    };

    const registeredTradingAddress = generateUKAddress(values);

    await submitRegistration({
      values: { ...values, registeredTradingAddress, uuid: inviteId },
      onSuccess: handleSuccess,
      onError: () => {
        setShowErrorModal(true);
      },
    });

    setIsLoading(false);
  };

  const renderCompanyInfo = () => {
    return (
      <>
        <h2>Company Information</h2>
        <Card className={styles.companyInfoContainer}>
          <div className={styles.formField}>
            <Select
              {...register('introducerType')}
              id="introducerType"
              name="introducerType"
              placeholder="Choose an option"
              onChange={(e) =>
                handleChangeIntroducerType(e.target.value as TIntroducerType)
              }
              className={styles.select}
              label="Introducer type"
              error={errors.introducerType && errors.introducerType.message}
            >
              <option value="accountant">Accounting / advisory firm</option>
              <option value="broker">Brokerage</option>
            </Select>
          </div>

          <div className={clsx(styles.formField, styles.companySearch)}>
            {isCompanySearch ? (
              <CompanyInputUK
                onChange={handleCompanySearchSelect}
                setSelectedCompany={setSelectedCompany}
                tooltip="Try using the company number if you cannot find the business you are looking for."
                error={errors.companyName && errors.companyName.message}
              />
            ) : (
              <Input
                {...register('companyName')}
                id="companyName"
                name="companyName"
                type="text"
                data-testid="company-name"
                label={
                  <div className={styles.companyNameLabel}>
                    <span className={styles.companyNameText}>Company name</span>
                    <Tooltip
                      dataTestId="company-search-tooltip"
                      content="Use company search if you are registered with Companies House."
                    />
                  </div>
                }
                placeholder="Your company name"
                error={errors.companyName && errors.companyName.message}
              />
            )}
            <Button
              variant="tertiary"
              size="small"
              className={styles.toggleCompanyInput}
              onClick={handleToggleCompanyInputMode}
              type="button"
            >
              {isCompanySearch
                ? introducerType === 'accountant'
                  ? 'Manual input'
                  : 'Set up as sole trader'
                : 'Use company search'}
            </Button>
          </div>
          <div className={styles.formField}>
            <Input
              {...register('addressLine1')}
              id="addressLine1"
              name="addressLine1"
              type="text"
              label="Address line 1"
              placeholder="2 Street"
              error={errors.addressLine1 && errors.addressLine1.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('addressLine2')}
              id="addressLine2"
              name="addressLine2"
              type="text"
              label={
                <>
                  Address line 2 <span>(optional)</span>
                </>
              }
              placeholder="Unit 12"
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('town')}
              id="town"
              name="town"
              type="text"
              label="Town / city"
              placeholder="London"
              error={errors.town && errors.town.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('postcode', {
                setValueAs: (text: string) => text.toUpperCase(),
              })}
              id="postcode"
              name="postcode"
              type="text"
              label="Postcode"
              placeholder="W1W 6XH"
              error={errors?.postcode?.message && errors.postcode.message}
              className={styles.uppercaseText}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('companyTradingName')}
              id="companyTradingName"
              name="companyTradingName"
              data-testid="company-trading-name"
              type="text"
              label={
                <>
                  Company trading name <span>(optional)</span>
                </>
              }
              placeholder="Iwoca"
              error={
                errors.companyTradingName && errors.companyTradingName.message
              }
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('companyWebsite')}
              id="companyWebsite"
              name="companyWebsite"
              type="text"
              label={
                <>
                  Company website <span>(optional)</span>
                </>
              }
              placeholder="www.iwoca.co.uk"
            />
          </div>
          {introducerType === 'broker' && (
            <div className={styles.formField}>
              <Input
                {...register('FCAReferenceNumber')}
                id="FCAReferenceNumber"
                name="FCAReferenceNumber"
                type="text"
                label={
                  <>
                    FCA reference number <span>(optional)</span>
                  </>
                }
                placeholder="123456"
              />
            </div>
          )}
        </Card>
      </>
    );
  };

  const renderAccountInfo = () => {
    return (
      <>
        <h2>Account Information</h2>
        <Card className={styles.accountInformationContainer}>
          <div className={styles.formField}>
            <Select
              {...register('title')}
              label="Title"
              id="title"
              name="title"
              type="text"
            >
              <option value="Mr">Mr</option>
              <option value="Ms">Ms</option>
              <option value="Mrs">Mrs</option>
              <option value="Miss">Miss</option>
            </Select>
          </div>
          {isCompanySearch && !inviteId ? (
            <div className={clsx(styles.formField, styles.directorName)}>
              <DirectorSearch
                onChange={handleDirectorSearchSelect}
                companyNumber={selectedCompany?.number}
                showCannotFindApplicantModal={false}
                label="Director's name"
                error={
                  (errors.firstName || errors.lastName) &&
                  'Director is required'
                }
              />
            </div>
          ) : (
            <>
              <div className={clsx(styles.formField, styles.firstName)}>
                <Input
                  {...register('firstName')}
                  id="firstName"
                  name="firstName"
                  type="text"
                  label="First Name"
                  placeholder="John"
                  error={errors.firstName && errors.firstName.message}
                />
              </div>
              <div className={clsx(styles.formField, styles.lastName)}>
                <Input
                  {...register('lastName')}
                  id="lastName"
                  name="lastName"
                  type="text"
                  label="Last Name"
                  placeholder="Smith"
                  error={errors.lastName && errors.lastName.message}
                />
              </div>
            </>
          )}

          <div className={clsx(styles.formField, styles.email)}>
            <Input
              {...register('email')}
              id="email"
              name="email"
              type="text"
              label="Email address"
              placeholder="me@iwoca.co.uk"
              error={errors.email && errors.email.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('phoneNumber')}
              id="phoneNumber"
              name="phoneNumber"
              type="text"
              label="Phone Number"
              placeholder="+44 123456789"
              error={errors.phoneNumber && errors.phoneNumber.message}
            />
          </div>
        </Card>
      </>
    );
  };

  return (
    <div className={clsx(styles.container, styles.ukForm)}>
      <h1>Sign up to create an introducer account</h1>
      {!inviteId && (
        <p className={styles.inviteInformation}>
          Is your company already partnered with us? Get a colleague to share a
          referral link to get started straight away.
        </p>
      )}
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        {!inviteId && renderCompanyInfo()}
        {renderAccountInfo()}
        <div className={styles.privacyPolicyAgreement}>
          <CheckBox
            {...register('privacyPolicyAgreement')}
            id="privacyPolicyAgreement"
            name="privacyPolicyAgreement"
            data-testid="privacy-policy-agreement"
          />
          <div className={styles.privacyPolicyDescription}>
            <p>
              <strong>
                I agree to iwoca carrying out due diligence on the company I
                represent and understand that iwoca will contact me for
                signature on the iwoca introducer agreement.
              </strong>
            </p>
            <p>
              Your privacy is important to us at iwoca, and we’re committed to
              protecting your personal data. We’ll store your personal data
              securely, and we’ll only get in touch in line with our{' '}
              <a
                href="https://www.iwoca.co.uk/introducer-privacy-policy/"
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </a>{' '}
              or if you opt in for extra communication from us.
            </p>
            {errors.privacyPolicyAgreement && (
              <Message
                message={errors.privacyPolicyAgreement.message || ''}
                type="error"
              />
            )}
          </div>
        </div>
        <div className={styles.footerItem}>
          <p className={styles.submitDescription}>
            We’ll process your registration.
          </p>
        </div>
        <div className={styles.footerItem}>
          <Button
            variant="primary"
            size="small"
            type="submit"
            loading={isLoading}
          >
            Create an account
            <Icon icon="lockCloseFilled" />
          </Button>
        </div>
      </form>
      <Modal
        closeIcon
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="There's a problem with the info you've entered."
        className={styles.modal}
      >
        <p className={styles.modalContent}>
          If you have an account with iwoca, use the login button in the top
          right, otherwise give us a call and we’ll help you get logged in.
        </p>
      </Modal>
    </div>
  );
};
